import type { ReactNode } from 'react'
import type { WorkerNotification } from '../types.js'
import { NotificationIcon } from './notificationIcons.js'
import { messageTitle } from './messageCards.js'

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** The behavior a message is about, in words, from its key: `on_time_arrival` reads "on-time arrival". */
function behaviorWords(key: string): string {
  return key.replace(/_/g, ' ').replace('on time', 'on-time')
}

/** What a card may do beyond being read: the shell supplies it per kind (answer a Bridge callout, open the Shifts calendar, read a reply). */
export type MessageActions = (notification: WorkerNotification) => ReactNode

export interface WorkerMessageCardProps {
  notification: WorkerNotification
  /** Tapping the card: marks it read, and on the Messages screen opens the "why was I told this" lines. */
  onTap?: (notification: WorkerNotification) => void
  /** True while the card shows what the message was built from. */
  isOpen?: boolean
  /** The buttons this kind of message carries, if any. */
  actions?: ReactNode
}

/**
 * One message as the worker reads it, as a card (plan section 7, the
 * demo's result cards): the icon for its kind, a heading saying what sort
 * of thing it is, the message, the action line, when it arrived and
 * whether it is new - and, where the kind has something to do, the buttons
 * for it right on the card. Shared by the Home preview and the Messages
 * screen, so the two never disagree.
 */
export function WorkerMessageCard({ notification, onTap, isOpen = false, actions }: WorkerMessageCardProps) {
  const unread = !notification.read
  const frame = `flex w-full gap-2.5 items-start border p-2.5 text-left ${unread ? 'bg-white border-marine/15' : 'bg-canvas border-marine/10'}`
  const body = (
    <>
      <NotificationIcon type={notification.type} size="md" />
      <div className="min-w-0 flex-1">
        <div className={`text-[11px] font-extrabold ${unread ? 'text-ink' : 'text-ink-mid'}`}>{messageTitle(notification.type)}</div>
        {notification.staffMessage && (
          <div className="text-[10px] text-ink-mid">
            From {notification.staffMessage.facilityName}
            {notification.staffMessage.sentBy ? ` · ${notification.staffMessage.sentBy}` : ''}
            {notification.staffMessage.urgent ? ' · urgent' : ''}
          </div>
        )}
        <div className={`text-[11px] leading-relaxed ${unread ? 'text-ink' : 'text-ink-mid'}`}>{notification.message}</div>
        {notification.action && <div className="text-[11px] text-ink font-semibold leading-relaxed">{notification.action}</div>}
        <div className="text-[10px] text-ink-mid/70 mt-0.5">
          {formatDateTime(notification.createdAt)}
          {unread && <span className="ml-1.5 text-coral font-bold">new</span>}
        </div>
        {isOpen && <BasedOn notification={notification} />}
      </div>
    </>
  )
  return (
    <div className={`${frame} flex-col`}>
      {onTap ? (
        <button type="button" onClick={() => onTap(notification)} aria-expanded={isOpen} className="flex w-full gap-2.5 items-start text-left hover:opacity-90">
          {body}
        </button>
      ) : (
        <div className="flex w-full gap-2.5 items-start">{body}</div>
      )}
      {actions && <div className="ml-9 flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

/** "Why was I told this", in words: what it is about, the points in play, and the score it was built from. */
export function BasedOn({ notification }: { notification: WorkerNotification }) {
  const lines: string[] = []
  if (notification.behaviorKey) {
    lines.push(`About ${behaviorWords(notification.behaviorKey)}.`)
  }
  if (notification.pointsAtStake !== null && notification.pointsAtStake !== undefined) {
    lines.push(`${notification.pointsAtStake.toFixed(1)} points in play.`)
  }
  if (notification.basedOn) {
    lines.push(`Based on your score as of ${formatDate(notification.basedOn.computedAt)}, for ${formatDate(notification.basedOn.from)} onward.`)
  }
  if (notification.expiresAt) {
    lines.push(`Stops applying ${formatDate(notification.expiresAt)}.`)
  }
  if (lines.length === 0) {
    return <div className="mt-2 text-[10px] text-ink-mid leading-relaxed border-t border-marine/10 pt-2">Sent from your record. No further detail was stored with it.</div>
  }
  return (
    <div className="mt-2 text-[10px] text-ink-mid leading-relaxed border-t border-marine/10 pt-2">
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  )
}
